import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Ikon } from '../status/Ikon.tsx';
import { useSmal } from './useSmalSkjerm.ts';

/**
 * Velgeren fra en knapp (H3.1, DD-48/DD-51 presisert: «en velger som legger til én kobling er en
 * meny fra en knapp, ikke et skjema») — `ux-flow-architect` «Meny fra en knapp»:
 *
 * - `role="dialog"` UTEN `aria-modal`, ingen fokusfelle, ingen scrim over 900 px. Siden bak er i bruk.
 * - `aria-expanded` på utløseren bærer tilstanden (utløseren eies av kalleren).
 * - Esc og pekertrykk utenfor lukker, og fokus går tilbake til utløseren.
 * - Fokus INN ved åpning — på søkefeltet, som er det første man gjør.
 * - Sidens taster blokkeres IKKE.
 * - Rendres UTENFOR ethvert rullende element (portal til `body`), og står fast under utløseren
 *   over 900 px. Posisjonen er MÅLT i vinduet, ikke en designverdi, og settes som CSS-variabler —
 *   regelen som bruker dem står i CSS-filen, som `Radmeny`. Under 900 px er den et ark fra bunnen.
 *
 * Samme skall for begge veier: hypotesene fra saken, og sakene fra hypotesen. Innholdet er kallerens.
 */
export function Velger({
  tittel,
  tittelId,
  utloeser,
  onLukk,
  children,
  fot,
  testid,
  onKeyDown,
}: {
  tittel: string;
  tittelId: string;
  utloeser: RefObject<HTMLElement | null>;
  onLukk: () => void;
  children: ReactNode;
  fot?: ReactNode;
  testid?: string;
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
}) {
  const smal = useSmal();
  const rot = useRef<HTMLDivElement>(null);
  const [plass, setPlass] = useState<CSSProperties>({});

  /** Lukker og gir fokus tilbake til utløseren — begge veier ett sted. */
  const lukk = useCallback(() => {
    onLukk();
    utloeser.current?.focus();
  }, [onLukk, utloeser]);

  /**
   * Under utløseren, venstrekanten med utløserens — men aldri utenfor vinduet: er det for trangt til
   * høyre, flyttes den inn; er det for trangt under, åpner den over utløseren. Margen er temaets
   * `space-md`, lest fra tokens.css, så komponenten ikke bærer et tall.
   */
  const maal = useCallback(() => {
    const r = utloeser.current?.getBoundingClientRect();
    const boks = rot.current;
    if (!r || !boks) return;
    const marg = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--innspel-space-md')) || 0;
    const venstre = Math.max(marg, Math.min(r.left, window.innerWidth - boks.offsetWidth - marg));
    let topp = r.bottom + marg / 2;
    if (topp + boks.offsetHeight > window.innerHeight - marg) topp = Math.max(marg, r.top - marg / 2 - boks.offsetHeight);
    const t = `${Math.round(topp)}px`;
    const v = `${Math.round(venstre)}px`;
    // Samme plass er ingen endring: en ny stil hver gang noe inni endrer seg, ville tegnet på nytt i det uendelige.
    setPlass((f) => {
      const g = f as Record<string, string>;
      return g['--velger-topp'] === t && g['--velger-venstre'] === v ? f : ({ '--velger-topp': t, '--velger-venstre': v } as CSSProperties);
    });
  }, [utloeser]);

  useLayoutEffect(() => {
    maal();
    // Lista inni vokser når den er hentet og krymper med søket: plassen måles på nytt.
    if (typeof ResizeObserver === 'undefined' || !rot.current) return undefined;
    const obs = new ResizeObserver(() => maal());
    obs.observe(rot.current);
    return () => obs.disconnect();
  }, [maal]);

  // Fokus INN ved åpning: søkefeltet, eller det første som kan trykkes.
  useEffect(() => {
    (rot.current?.querySelector<HTMLElement>('input') ?? rot.current?.querySelector<HTMLElement>('button:not([disabled])'))?.focus();
  }, []);

  useEffect(() => {
    const ned = (e: PointerEvent) => {
      const el = e.target instanceof Node ? e.target : null;
      if (el && (rot.current?.contains(el) || utloeser.current?.contains(el))) return;
      onLukk();
    };
    const tast = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') lukk();
    };
    // Rulling flytter utløseren: velgeren følger etter i stedet for å lukke seg midt i et søk.
    window.addEventListener('scroll', maal, true);
    window.addEventListener('resize', maal);
    document.addEventListener('pointerdown', ned);
    document.addEventListener('keydown', tast);
    return () => {
      window.removeEventListener('scroll', maal, true);
      window.removeEventListener('resize', maal);
      document.removeEventListener('pointerdown', ned);
      document.removeEventListener('keydown', tast);
    };
  }, [lukk, maal, onLukk, utloeser]);

  return createPortal(
    <div ref={rot} role="dialog" aria-labelledby={tittelId} className={smal ? 'panel-velger panel-velger-ark' : 'panel-velger'} style={smal ? undefined : plass} data-testid={testid} onKeyDown={onKeyDown}>
      <div className="panel-velger-hode">
        <b id={tittelId}>{tittel}</b>
        <button type="button" className="panel-velger-lukk" aria-label="Lukk" onClick={lukk}>
          <Ikon navn="close-outline" />
        </button>
      </div>
      {children}
      {fot ? <div className="panel-velger-fot">{fot}</div> : null}
    </div>,
    document.body,
  );
}
